import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import {
  addProjectValueAdds,
  deleteProjectValueAdd,
  fetchProjectValueAdds,
  searchProjectHealthByProjectId,
  updateProjectValueAdd,
} from "./projectReview.service";
import { mapApiProjectToProject, mapApiValueAddItems } from "./projectReview.utils";

const VALUE_ADDS_QUERY_KEY = (projectId) => ["project-review", "value-adds", projectId];

/** Looks up project-health details by id; shared by the header hydration and the Leadership Attention tab. */
export function useProjectHealthSearchQuery(projectId, enabled) {
  return useQuery({
    queryKey: ["project-review", "health-search", projectId],
    queryFn: () => searchProjectHealthByProjectId(projectId),
    enabled: enabled && Boolean(projectId),
    staleTime: 0,
    refetchOnMount: "always",
    retry: false,
    select: (data) => (data ? mapApiProjectToProject(data) : null),
  });
}

export function useValueAddsQuery(projectId, ownerFallback, enabled) {
  return useQuery({
    queryKey: VALUE_ADDS_QUERY_KEY(projectId),
    queryFn: () => fetchProjectValueAdds(projectId),
    enabled: enabled && Boolean(projectId),
    staleTime: 0,
    refetchOnMount: "always",
    retry: false,
    select: (data) => ({
      items: mapApiValueAddItems(data.valueAddList || [], ownerFallback),
      totalCount: data.totalValueAdds ?? (data.valueAddList || []).length,
    }),
  });
}

export function useAddValueAddsMutation(projectId) {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (values) => addProjectValueAdds(projectId, values),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: VALUE_ADDS_QUERY_KEY(projectId) }),
  });
}

export function useUpdateValueAddMutation(projectId) {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ valueAddId, payload }) =>
      updateProjectValueAdd(projectId, valueAddId, payload),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: VALUE_ADDS_QUERY_KEY(projectId) }),
  });
}

export function useDeleteValueAddMutation(projectId) {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (valueAddId) => deleteProjectValueAdd(projectId, valueAddId),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: VALUE_ADDS_QUERY_KEY(projectId) }),
  });
}
